import React, {
    useEffect, useRef, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Text, Divider, Icon
} from 'components/ui';
import Loader from 'components/Loader';
import ErrorMsg from 'components/ErrorMsg';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import DefaultPaymentCheckbox from 'components/RwdCheckout/Sections/Payment/Section/DefaultPaymentCheckbox';
import {
    colors, fontSizes, fontWeights, mediaQueries, space
} from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import KlarnaPaymentMethodEN from 'components/Klarna/KlarnaPaymentMethod/locales/KlarnaPaymentMethod_en_US';
import KlarnaPaymentMethodFR from 'components/Klarna/KlarnaPaymentMethod/locales/KlarnaPaymentMethod_fr_CA';

/**
 * Klarna Expanded View (L3 State) - Release 4
 *
 * Displays Klarna payment details inline within the Payment card.
 * Follows the same pattern as AfterPay L3 expansion with full Figma compliance.
 */
const KlarnaExpandedView = ({
    klarnaApiData = null,
    onSaveAndContinue,
    onCancel,
    loadWidget = null,
    klarnaHeader = 'Pay with Klarna',
    klarnaSubtitle = '',
    klarnaSaveAndContinue = 'Save and Continue',
    klarnaCancel = 'Cancel',
    klarnaLoading = 'Loading Klarna payment options...',
    klarnaApiErrorFallback = 'Unable to load Klarna payment information. Please try again or choose another payment method.',
    klarnaGiftCardWarning = '',
    klarnaFeatureBuyerProtection = '',
    klarnaTermsText = '',
    klarnaBillingAddressCheckbox = 'My billing address is the same as my shipping address',
    isLoading = false,
    errorMessage = null,
    installmentValue = '',
    isAnonymous = false,
    isBopisOrder = false,
    useMyShippingAddress = false,
    onBillingAddressToggle = null
}) => {
    const headerRef = useRef(null);
    const widgetLoadedRef = useRef(false);
    const containerRef = useRef(null);
    const [widgetHasContent, setWidgetHasContent] = useState(false);

    const locale = localeUtils.isCanada() ? (localeUtils.isFrench() ? 'fr_ca' : 'en_ca') : 'en_us';

    const klarnaLegalNotice = useMemo(() => {
        const getText = localeUtils.getLocalisedDataFromResource(KlarnaPaymentMethodEN, KlarnaPaymentMethodFR);
        const klarnaTermsUrl = `https://cdn.klarna.com/1.0/shared/content/legal/terms/0/${locale}/user`;
        const klarnaPrivacyUrl = `https://cdn.klarna.com/1.0/shared/content/legal/terms/0/${locale}/privacy`;

        return getText('legalNotice', [klarnaTermsUrl, klarnaPrivacyUrl]);
    }, [locale]);

    // Focus header when component mounts
    useEffect(() => {
        const previouslyFocused = document.activeElement;

        if (headerRef.current) {
            headerRef.current.focus();
        }

        return () => {
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                setTimeout(() => previouslyFocused.focus(), 0);
            }
        };
    }, []);

    /**
     * Load Klarna widget after component mounts.
     * Ensures DOM element exists before calling SDK.
     */
    useEffect(() => {
        if (loadWidget && !widgetLoadedRef.current && klarnaApiData?.widgetElementId) {
            const elementId = klarnaApiData.widgetElementId;

            widgetLoadedRef.current = true;

            setTimeout(() => {
                const element = document.getElementById(elementId);

                if (element) {
                    loadWidget();
                } else {
                    setTimeout(() => {
                        const retryElement = document.getElementById(elementId);

                        if (retryElement) {
                            loadWidget();
                        }
                    }, 200);
                }
            }, 100);
        }
    }, [loadWidget, klarnaApiData?.widgetElementId]);

    /**
     * Handle Escape key to close expanded view
     */
    useEffect(() => {
        const handleEscape = event => {
            if (event.key === 'Escape') {
                onCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onCancel]);

    /**
     * Monitor widget container for content after loading.
     */
    useEffect(() => {
        if (!klarnaApiData?.widgetElementId || isLoading) {
            return undefined;
        }

        const checkForContent = () => {
            const container = document.getElementById(klarnaApiData.widgetElementId);

            if (container && container.children.length > 0) {
                setWidgetHasContent(true);

                return true;
            }

            return false;
        };

        if (checkForContent()) {
            return undefined;
        }

        const container = document.getElementById(klarnaApiData.widgetElementId);

        if (!container) {
            return undefined;
        }

        const observer = new MutationObserver(() => {
            if (checkForContent()) {
                observer.disconnect();
            }
        });

        observer.observe(container, { childList: true, subtree: true });

        const timeoutId = setTimeout(() => {
            observer.disconnect();
        }, 3000);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [klarnaApiData?.widgetElementId, isLoading]);

    /**
     * Renders Klarna widget with loading, error, and success states
     * Collapses completely when SDK doesn't return content
     */
    const renderWidgetContent = () => {
        if (errorMessage) {
            return (
                <Box py={4}>
                    <ErrorMsg message={errorMessage} />
                    <Box
                        mt={3}
                        css={styles.fallbackText}
                    >
                        {klarnaApiErrorFallback}
                    </Box>
                </Box>
            );
        }

        // Only show widget area when loading OR when widget has actual content
        const shouldShowWidgetArea = isLoading || widgetHasContent;

        if (!shouldShowWidgetArea) {
            return null;
        }

        return (
            <Box css={styles.widgetArea}>
                {isLoading && (
                    <Box
                        py={3}
                        textAlign='center'
                    >
                        <Loader isShown={true} />
                        <Box
                            mt={3}
                            css={styles.loadingText}
                        >
                            {klarnaLoading}
                        </Box>
                    </Box>
                )}

                {klarnaApiData?.widgetElementId && (
                    <Box css={widgetHasContent ? styles.sdkWidgetWrapper : styles.sdkWidgetWrapperHidden}>
                        <div
                            id={klarnaApiData.widgetElementId}
                            data-testid='klarna-widget-container'
                            style={styles.widgetContainerLoaded}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    // Format the subtitle with the installment value
    const formattedSubtitle = klarnaSubtitle?.replace('{0}', installmentValue || '');

    return (
        <Box
            ref={containerRef}
            role='region'
            aria-labelledby='klarna-expanded-header'
            data-testid='klarna-expanded-view'
            data-at={Sephora.debug.dataAt('klarna_expanded_view')}
        >
            {/* Header Section */}
            <Box>
                <Text
                    as='h3'
                    id='klarna-expanded-header'
                    ref={headerRef}
                    tabIndex={-1}
                    css={styles.header}
                >
                    {klarnaHeader}
                </Text>
                {formattedSubtitle && <Text css={styles.subtitle}>{formattedSubtitle}</Text>}
            </Box>

            {/* Gift Card Warning Message */}
            {klarnaGiftCardWarning && <Box css={styles.giftCardWarning}>{klarnaGiftCardWarning}</Box>}

            {/* Feature Bullet Point - Buyer Protection */}
            {klarnaFeatureBuyerProtection && (
                <Box css={styles.featureList}>
                    <Box css={styles.featureItem}>
                        <Icon
                            name='checkmark'
                            size={12}
                            css={styles.checkIcon}
                        />
                        <Text
                            is='span'
                            dangerouslySetInnerHTML={{ __html: klarnaFeatureBuyerProtection }}
                            css={styles.featureText}
                        />
                    </Box>
                </Box>
            )}

            <Divider css={styles.divider} />

            {/* Terms Text with Links */}
            {klarnaTermsText && (
                <Box css={styles.termsText}>
                    <Text
                        is='p'
                        dangerouslySetInnerHTML={{ __html: klarnaTermsText }}
                    />
                </Box>
            )}

            {/* Widget Content Area */}
            <Box
                aria-live='polite'
                aria-busy={isLoading}
            >
                {renderWidgetContent()}
            </Box>

            {/* Billing Address Checkbox */}
            {!isBopisOrder && onBillingAddressToggle && (
                <Box css={styles.checkboxContainer}>
                    <Checkbox
                        checked={useMyShippingAddress}
                        onClick={onBillingAddressToggle}
                    >
                        {klarnaBillingAddressCheckbox}
                    </Checkbox>
                </Box>
            )}

            {/* Default Payment Checkbox */}
            {!isAnonymous && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox paymentName='klarna' />
                </Box>
            )}

            {/* Legal Notice */}
            {klarnaLegalNotice && (
                <Box css={styles.legalNotice}>
                    <Text
                        is='p'
                        dangerouslySetInnerHTML={{ __html: klarnaLegalNotice }}
                    />
                </Box>
            )}

            <Divider css={styles.divider} />

            {/* Action Buttons */}
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='klarna-cancel'
                    data-at={Sephora.debug.dataAt('klarna_cancel_button')}
                    aria-label='Cancel Klarna selection and return'
                >
                    {klarnaCancel}
                </Button>

                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='klarna-save-continue'
                    data-at={Sephora.debug.dataAt('klarna_save_continue_button')}
                    aria-label='Save Klarna and return to payment options'
                    disabled={isLoading || !!errorMessage}
                >
                    {klarnaSaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: '18px',
        marginTop: space[2],
        marginBottom: space[1]
    },
    subtitle: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '18px',
        [mediaQueries.xsMax]: {
            color: colors.gray
        }
    },
    giftCardWarning: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '18px',
        marginTop: space[2],
        marginBottom: space[2],
        [mediaQueries.xsMax]: {
            fontSize: `${fontSizes.xs}px`
        }
    },
    featureList: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    featureItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: space[2]
    },
    checkIcon: {
        color: colors.black,
        flexShrink: 0,
        marginTop: 2
    },
    featureText: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        lineHeight: '18px',
        '& a': {
            color: colors.black,
            textDecoration: 'underline'
        }
    },
    termsText: {
        color: colors.gray,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '18px',
        marginTop: space[2],
        marginBottom: space[3],
        '& a': {
            color: colors.gray,
            textDecoration: 'underline'
        }
    },
    widgetArea: {
        marginTop: space[2],
        marginBottom: space[3],
        width: '100%'
    },
    sdkWidgetWrapper: {
        backgroundColor: colors.nearWhite,
        borderRadius: 4,
        border: `1px solid ${colors.lightGray}`,
        padding: space[3],
        maxWidth: 410,
        width: '100%'
    },
    sdkWidgetWrapperHidden: {
        display: 'none'
    },
    widgetContainerLoaded: {
        opacity: 1,
        height: 'auto',
        overflow: 'visible'
    },
    loadingText: {
        fontSize: `${fontSizes.sm}px`,
        color: colors.gray
    },
    fallbackText: {
        fontSize: `${fontSizes.sm}px`,
        color: colors.gray
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    legalNotice: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '18px',
        marginTop: space[2],
        marginBottom: space[3],
        '& a': {
            color: colors.blue,
            textDecoration: 'underline'
        },
        [mediaQueries.xsMax]: {
            fontSize: `${fontSizes.xs}px`,
            '& a': {
                textDecoration: 'none'
            }
        }
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

KlarnaExpandedView.propTypes = {
    klarnaApiData: PropTypes.shape({
        widgetElementId: PropTypes.string,
        clientToken: PropTypes.string
    }),
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    loadWidget: PropTypes.func,
    klarnaHeader: PropTypes.string,
    klarnaSubtitle: PropTypes.string,
    klarnaSaveAndContinue: PropTypes.string,
    klarnaCancel: PropTypes.string,
    klarnaLoading: PropTypes.string,
    klarnaApiErrorFallback: PropTypes.string,
    klarnaGiftCardWarning: PropTypes.string,
    klarnaFeatureBuyerProtection: PropTypes.string,
    klarnaTermsText: PropTypes.string,
    klarnaLegalNotice: PropTypes.string,
    klarnaBillingAddressCheckbox: PropTypes.string,
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
    installmentValue: PropTypes.string,
    isAnonymous: PropTypes.bool,
    isBopisOrder: PropTypes.bool,
    useMyShippingAddress: PropTypes.bool,
    onBillingAddressToggle: PropTypes.func
};

export default wrapFunctionalComponent(KlarnaExpandedView, 'KlarnaExpandedView');
